import React, { useContext, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  fetchPsfServiceCesAllYears,
  fetchPsfServiceCesMonths,
} from "../psfSalesServiceKpi";
import { FilterContext } from "../../../context/Filter";

// Register Chart.js components + datalabels
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const PsfServiceCustomerEffortGraph = () => {
  const [psfCesData, setPsfCesData] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [loading, setLoading] = useState(true);

  const { arn: parArn, childArn: rawChildArn } = useContext(FilterContext);
  const childArn = rawChildArn || "";

  /** ---------- FETCH HELPERS ---------- **/

  // Fetch all years (default view)
  const fetchAllYearsData = async () => {
    setLoading(true);
    try {
      const response = await fetchPsfServiceCesAllYears(parArn, childArn);
      setYears(response.map((item) => item.key));
      if (!selectedYear) {
        setPsfCesData(response);
      }
    } catch (error) {
      console.error("Error fetching all years CES data:", error);
      setPsfCesData([]);
      setYears([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch month data for selected year
  const fetchMonthData = async (year) => {
    setLoading(true);
    try {
      const response = await fetchPsfServiceCesMonths(parArn, childArn, year);
      setPsfCesData(response);
    } catch (error) {
      console.error("Error fetching monthly CES data:", error);
      setPsfCesData([]);
    } finally {
      setLoading(false);
    }
  };

  /** ---------- EFFECTS ---------- **/
  useEffect(() => {
    fetchAllYearsData();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchMonthData(selectedYear);
    } else {
      fetchAllYearsData();
    }
  }, [selectedYear]);

  /** ---------- CONDITIONAL STATES ---------- **/
  if (loading) {
    return (
      <div className="w-full h-[250px] flex items-center justify-center text-gray-500 font-medium">
        Loading...
      </div>
    );
  }

  if (!psfCesData || psfCesData.length === 0) {
    return (
      <div className="w-full h-[250px] flex items-center justify-center text-gray-500 font-medium">
        No Data Available
      </div>
    );
  }

  /** ---------- CHART CONFIG ---------- **/
  const labels = psfCesData.map((item) => item.key);
  const dataValues = psfCesData.map((item) => item.ces);

  const chartData = {
    labels,
    datasets: [
      {
        label: "PSF Customer Effort Score",
        data: dataValues,
        backgroundColor: "#5b9bd5",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 30 } },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      datalabels: {
        anchor: "end",
        align: "end",
        clip: false,
        formatter: (value) => `${value}%`,
        font: { weight: "bold", size: 14 },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: { display: false, beginAtZero: true, max: 100 },
    },
  };

  /** ---------- UI RENDER ---------- **/
  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-4">
      {/* Year Dropdown */}
      <select
        value={selectedYear}
        onChange={(e) => setSelectedYear(e.target.value)}
        className="border rounded p-2 mb-4"
      >
        <option value="">All Years</option>
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>

      {/* Bar Chart */}
      <div className="w-full max-w-md" style={{ height: "250px" }}>
        <Bar data={chartData} options={chartOptions} height={250} />
      </div>
    </div>
  );
};

export default PsfServiceCustomerEffortGraph;
